<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    :id="vnodeId"
    class="cvt-editor-text"
    :class="editorClasses"
    draggable="false"
    @mouseover="target"
    @click="enableEditor"
    @dblclick="resizeContent"
    @contextmenu="showContextMenuIfNotEditing($event)"
    @dragstart="dragstart"
    @dragend="dragend"
  >
    <drag-and-drop-zone
      :z-index="dropzoneZIndex"
      @dropped="addElementToSide"
    />

    <!-- main vdom node -->
    <div
      class="draft-editor"
      :class="draftStyleClasses"
    >
      <div
        class="froala-editor"
        v-html="contentsHTML"
      />
    </div>
    <template v-if="showActionButtonsOnClick && !isGridBuilder">
      <!--THis is only here because of freshwork in v1 gridbuilder,
      once they migrate to v2 or v3, it will become irrelevant-->
      <action-buttons
          v-if="selected"
          @duplicate="duplicateAndValidate"
          @remove="removeAndValidate"
      />
    </template>
  </div>
</template>

<script>
import FroalaEditor from 'froala-editor/js/froala_editor.min.js'
import * as _ from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import { rgbToHex } from '../../../lib/color-utils'
import { FONT_SIZES } from '../../../lib/font.js'
import * as getters from '../../../store/modules/getters'
import * as mutations from '../../../store/modules/mutations'
import FbVNode from '../../base/FbVNode.vue'

export default FbVNode.extend({
  components: {},
  props: {
    contents: Object,
    contentsHTML: String
  },
  computed: {
    ...mapGetters({
      range: getters.DRAFT_RANGE,
      rangeFormat: getters.DRAFT_RANGE_FORMAT,
      colors: getters.THEME_GET_COLORS,
      availableFonts: getters.THEME_AVAILABLE_FONTS,
      typography: getters.THEME_TYPOGRAPHY,
      theme: getters.THEME,
      advancedMenuOpen: getters.ADVANCED_MENU_OPEN,
    }),
    editorClasses () {
      return {
        ...this.classes,
        'w-100': this.isGridBuilder
      }
    },
    draftStyleClasses () {
      return {}
    },
    styles () {
      return {}
    }
  },
  data () {
    return {
      name: 'Draft',
      draftToolsVisible: false,
      isDirty: false
    }
  },
  mounted () {
    this.init()
  },
  destroyed () {
    this.shutdown()
  },
  methods: {
    ...mapMutations({
      updateDraftRange: mutations.DRAFT_UPDATE_RANGE,
      setFroalaInstance: mutations.DRAFT_SET_FROALA_INSTANCE,
      updateDraftRangeFormat: mutations.DRAFT_UPDATE_RANGE_FORMAT,
      updateTypography: mutations.THEME_UPDATE_DATA
    }),

    showContextMenuIfNotEditing (e) {
      // There is this strange issue which occurs when user is using old-builder and he right-clicks on text-element which
      // is being edited to open the context menu. Then sometimes accessing the hasFocus method of text-element(node) being edited throws error. 
      // As per my initial oberservation, this is basically due to the plugin not behaving correctly. The optional-chaining is quick fix for it.
      if (!this.froala?.node?.hasFocus?.()) {
        e.stopPropagation()
      }
    },

    resizeContent () {
      this.resize()
    },

    getHexColors () {
      // Convert THEME_RGBA_COLOR to HEX format
      return Object.keys(this.colors).map((key) => {
        let r = this.colors[key]['color'][0]
        let g = this.colors[key]['color'][1]
        let b = this.colors[key]['color'][2]
        return rgbToHex(r, g, b)
      })
    },

    getFontFamily () {
      let fontFamily = {}
      this.availableFonts.forEach(font => {
        fontFamily[`${font.familyName}, ${font.genericFamily}`] = font.familyName
      })
      return fontFamily
    },

    getFontFamilyName (font) {
      return font.split('-').map(a => a.charAt(0).toUpperCase() + a.slice(1)).join(' ')
    },

    getFontSizes () {
      return FONT_SIZES.map(fontSize => {
        return fontSize.value
      })
    },

    init () {
      let hexColors = this.getHexColors()
      let fontFamily = this.getFontFamily()
      let fontSizes = this.getFontSizes()

      let styleFonts = {}
      let styleFontSize = {}

      Object.keys(this.typography).forEach(key => {
        styleFonts[key] = this.typography[key]['font']
        styleFontSize[key] = this.typography[key]['size']
      })

      let config = {
        key: '9KH3cC4B3C3A1C1F3zPAENHMf1JPQRFZBTBf1WWEPYDbB3H3E2A14A19B7B5C6A2C3==',
        toolbarInline: true,
        toolbarContainer: '#froala-portal',
        charCounterCount: true,
        toolbarVisibleWithoutSelection: true,
        initOnClick: true,
        keepFormatOnDelete: true,
        pastePlain: true,
        pasteDeniedTags: ['div'],
        pasteDeniedAttrs: ['class', 'style'],
        paragraphStyles: {
          'font-style-title': '<h1>Title</h1>',
          'font-style-subtitle': '<h2>SubTitle</h2>',
          'font-style-heading': '<h4>Heading</h4>',
          'font-style-normalText': '<p>Normal Text</p>'
        },
        paragraphMultipleStyles: false,
        toolbarButtons: {
          moreMisc: {
            buttons: ['paragraphStyle','textColor', 'bold', 'italic', 'underline', 'clearFormatting',
              'alignLeft', 'alignCenter', 'alignRight', 'insertLink', 'undo', 'redo', 'fontFamily',
              'fontSize', 'lineHeight', 'backgroundColor', 'strikeThrough', 'formatOLSimple', 'formatUL'],
            buttonsVisible: 13
          }
        },
        colorsText: hexColors,
        colorsBackground: hexColors,
        fontFamilySelection: true,
        fontFamily: fontFamily,
        fontSize: fontSizes,
        lineHeights: {
          Default: '',
          Single: '1',
          '1.15': '1.15',
          '1.5': '1.5',
          Double: '2'
        },
        linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove', 'textColor', 'backgroundColor'],
        linkAutoPrefix: 'https://',
        events: {
          'initialized': function () {
            console.log('initialized')
          },
          'commands.after': function (e, param1, param2) {
             if (e === 'paragraphStyle')
            {
              if(param1 === 'font-style-title')
              {
                this.paragraphFormat.apply('h1');
              }
              else if(param1 === 'font-style-subtitle')
              {
                this.paragraphFormat.apply('h2');
              }
              else if(param1 === 'font-style-heading')
              {
                this.paragraphFormat.apply('h4');
              }
              else if(param1 === 'font-style-normalText')
              {
                this.paragraphFormat.apply('p');
              }
            }
          },
          'contentChanged': () => {
            if (this.isGridBuilder) this.resize()
            this.autoSave()
          }
        }
      }
      this.editor = this.$el.querySelector('.draft-editor')

      this.froala = new FroalaEditor(this.editor, config, function () {
      })
    },

    shutdown () {
      if (this.editor) {
        this.froala.destroy()
      }
    },

    enableEditor (e) {
      if (this.isGridBuilder && !this.advancedMenuOpen) return
      if (!this.selected) {
        // this.init()
        this.select(e)
        if (this.froala && this.froala.edit) {
          this.froala.edit.on()
        }
        if (this.froala && this.froala.events) {
          this.froala.events.focus()
          this.froala.events.enableBlur()
        }
      }
    },

    getHtml () {
      return this.froala.root.innerHTML
    },
    autoSave: _.debounce(async function() {
      this.froala.selection?.save()
      this.isDirty = true
      await this.save()
      this.isDirty = false
      this.froala.selection?.restore()
    }),
    save () {
      if (this.froala && this.froala.html) {

        let htmlStr = this.froala.html.get(true)

        // old code
        // htmlStr = htmlStr.split('<div class="froala-editor">').join('');
        // htmlStr = htmlStr.split('</div>').join('');

        // new code
        // if this line exists ('<div class="froala-editor">'), then and only then replace this and also its closing div.
        // previously, it was removing the closing div even if '<div class="froala-editor">' does not exist
        // that is why, it was causing the HTML nesting and text-compression issue 
        if( htmlStr.search('<div class="froala-editor">') >= 0 ) 
        {
          htmlStr = htmlStr.replace('<div class="froala-editor">', ''); // replaces the first occurance
          htmlStr = htmlStr.replace(/<\/div>$/, '');                    // replaces the last </div> tag in the HTML string
        }
          
        // this makes sure no empty string is saved in firebase which makes froala element non-editable
        htmlStr = htmlStr === '' ? '<br>' : htmlStr;
         
        return Promise.all([
          // this.self().child('data/props/contents').set(this.froala.getContents()),
          this.updateProp('contentsHTML', htmlStr)
        ])
      } else {
        return Promise.all([])
      }
    },
  },
  watch: {
    selected (selectedNow, previouslySelected) {
      if (previouslySelected && !selectedNow && this.froala) {
        this.save()
        // this.froala.edit.off()
        // this.froala.disable()
        // this.froala.blur()
        // this.froala.events.disableBlur()
      }
    },
    contentsHTML: {
      handler (contentsHTML, oldContentsHTML) {
        this.froala?.html?.set?.(contentsHTML)
      }
    },
    colors: function(val) {
      this.froala.destroy();
      this.init();
    }
  }
})
</script>

<style lang="scss">
  @import "@/scss/utils";

  .cvt-editor-text {
    position: relative;
  }

  .froala-editor {
    min-width: 50px;
    padding: 0;
    overflow-y: visible;
    p {
      margin-bottom: 0;
      min-height: 50px;
    }
  }

  .layout-style-1  {
    .oldbuilder {
      .fr-toolbar {
        top: 100px !important;
      }
    }
    .gridbuilder {
      .fr-toolbar {
        top: 72px !important;
      }
    }
  }

  .layout-style-2  {
    .oldbuilder {
      .fr-toolbar {
        top: 80px !important;
      }
    }
    .gridbuilder {
      .fr-toolbar {
        top: 80px !important;
      }
    }
  }

  .fr-toolbar{
    position: fixed;
    border-radius: 10px !important;
    width: 720px;
    z-index: 21;

    bottom: unset !important;
    left: unset !important;
    right: unset !important;

    margin-left: auto !important; 
    margin-right: auto !important; 
  }

  .fr-toolbar.fr-top {
    border-bottom: 1px solid rgb(204, 204, 204) !important;
  }

  .fr-expanded {
    border-bottom-right-radius: 10px !important;
    border-bottom-left-radius: 10px !important;
  }
  .fr-toolbar .fr-command.fr-btn span {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }
</style>
